import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import isEmpty from 'lodash/isEmpty'
import Mixin from 'components/common/Mixin'
import Media from 'components/common/Media'
import Icon from 'components/common/Icon/Icon'
import Theme from 'components/common/Theme/Theme'
import Button from 'components/common/Button/Button'
import Circle from 'components/common/Circle/Circle'
import Link, { A } from 'components/common/Link/Link'
import PhoneLink from 'components/common/PhoneLink'
import InfinityScroll from 'components/common/InfinityScroll'
import SpinnerIcon from 'components/common/SpinnerIcon/SpinnerIcon'
import { formatPhoneNumber } from 'helpers/phone-number'
import { getProductUniqueId } from './helpers'
import { TIRES_LANDING_PAGE, STORE_LOCATOR_PAGE, SERP_CONTEXT_TYPE } from 'constant'
import {
  SEARCH_PROMOTION_TYPE,
  NORESULT_UHNO,
  NORESULT_FIRST_SENTENCE,
  NORESULT_NOSTORE
} from '../constants'
import ProductBlock from '../components/ProductBlock'
import FiltersModal from '../components/FiltersModal'
import Promotion from '../components/Promotion'
import SortByForm from '../SortByForm'

const TireResultList = ({
  totalNumRecs,
  records = [],
  viewAllRecords,
  noNewRecords,
  sortOptions,
  trayItemsCount,
  compareBtnText,
  compareTrayItems,
  toggleCompareTray,
  handleViewAllClick,
  handlePromotionClick,
  infinityScrollHandler,
  showSpinner,
  storePhoneNumber,
  noResultsSuffix,
  tiresLandingPageCTAVerbiage,
  oeFilterMode,
  oeFilterItems
}) =>
  <Products>
    {
      Boolean(totalNumRecs) &&
        <Sort>
          <Media.Desktop>
            <SortByForm sortOptions={ sortOptions } />
          </Media.Desktop>
          <Media.Mobile>
            <FiltersModal />
          </Media.Mobile>
          <Compare data-at-compare-button narrow disabled={ !trayItemsCount } secondary full="mobile" onClick={ () => toggleCompareTray() }>
            { compareBtnText }
            <CircleIcon disabled={ !trayItemsCount }>
              <Count>
                { trayItemsCount }
              </Count>
            </CircleIcon>
          </Compare>
        </Sort>
    }

    {
      oeFilterMode &&
      oeFilterItems.map(({ type, promotion, ...product } = {}, index) =>
        type === SEARCH_PROMOTION_TYPE ?
          !isEmpty(promotion) &&
          <Product key={ index }>
            <Promotion { ...promotion } onClick={ event => handlePromotionClick({ event, item: product, index }) } />
          </Product> :
          <Product data-at-serp-tire key={ getProductUniqueId({ product, index }) }>
            <ProductBlock
              product={ product }
              index={ index }
              compareTrayItems={ compareTrayItems }
              contextType={ SERP_CONTEXT_TYPE } />
          </Product>
      )
    }

    {
      !oeFilterMode &&
      <>
        {totalNumRecs ?
          records.map(({ type, promotion, ...product } = {}, index) =>
            type === SEARCH_PROMOTION_TYPE ?
              !isEmpty(promotion) &&
              <Product key={ index }>
                <Promotion
                  { ...promotion }
                  onClick={ event => handlePromotionClick({ event, item: product, index }) } />
              </Product> :
              <Product
                data-at-serp-tire
                key={ getProductUniqueId({ product, index }) }>
                <ProductBlock
                  product={ product }
                  index={ index }
                  compareTrayItems={ compareTrayItems }
                  contextType={ SERP_CONTEXT_TYPE } />
              </Product>
          ) : <NoResultBlock>
            <NoResultTitle>{NORESULT_UHNO}</NoResultTitle>
            <NoResultMessage>{NORESULT_FIRST_SENTENCE}</NoResultMessage>
            {
              storePhoneNumber ?
                <Fragment>
                  <Media.Desktop>
                    <NoResultMessage>{`Please contact your store at ${formatPhoneNumber(storePhoneNumber)} for assistance, ${noResultsSuffix}`}</NoResultMessage>
                  </Media.Desktop>
                  <Media.Mobile>
                    <NoResultMessage>
                      Please contact your store at <PhoneLink number={ storePhoneNumber } />{' '}
                      for assistance, {noResultsSuffix}
                    </NoResultMessage>
                  </Media.Mobile>
                </Fragment> :
                <Fragment>
                  <NoResultMessage>
                    {`${NORESULT_NOSTORE}${noResultsSuffix}`}
                  </NoResultMessage>
                  <StyledButtonLink button to={ STORE_LOCATOR_PAGE }>Choose a Store</StyledButtonLink>
                </Fragment>
            }
            <StyledButtonLink button to={ TIRES_LANDING_PAGE }>{tiresLandingPageCTAVerbiage}</StyledButtonLink>
          </NoResultBlock>
        }
        {
          totalNumRecs && !viewAllRecords ?
            <ViewAllSection data-at-viewall-section>
              <span data-at-viewall-section-caption>Didn’t find what you are looking for?</span>
              <View prevent handler={ handleViewAllClick }>
                View All {totalNumRecs} Results<Icon asset="caret-blue-down" margin="-2px 0 0 5px" alt="" />
              </View>
            </ViewAllSection> :
            <InfinityScroll scrollPadding={ 800 } handler={ infinityScrollHandler }>
              {
                showSpinner && !noNewRecords &&
                <SpinnerWrapper>
                  <InfinitySpinnerIcon />
                </SpinnerWrapper>
              }
            </InfinityScroll>
        }
      </>
    }
  </Products>

TireResultList.propTypes = {
  compareBtnText: PropTypes.string,
  compareTrayItems: PropTypes.arrayOf(PropTypes.shape()),
  handleClearFilterClick: PropTypes.func,
  handlePromotionClick: PropTypes.func,
  handleViewAllClick: PropTypes.func,
  infinityScrollHandler: PropTypes.func,
  noNewRecords: PropTypes.bool,
  noResultsLink: PropTypes.string,
  noResultsSuffix: PropTypes.string,
  oeFilterItems: PropTypes.arrayOf(PropTypes.shape()),
  oeFilterMode: PropTypes.bool,
  records: PropTypes.arrayOf(PropTypes.shape()),
  showSpinner: PropTypes.bool,
  sortOptions: PropTypes.arrayOf(PropTypes.shape()),
  storePhoneNumber: PropTypes.string,
  tiresLandingPageCTAVerbiage: PropTypes.string,
  toggleCompareTray: PropTypes.func,
  totalNumRecs: PropTypes.number,
  trayItemsCount: PropTypes.number,
  viewAllRecords: PropTypes.bool
}

export default TireResultList

const Product = styled.div`
  position: relative;
  margin-bottom: 30px;
  border: 1px solid ${Theme.colors.light_grey};
`

const Products = styled.div`
  ${Media.desktop`
    display: table-cell;
    padding: 0 0 0 24px;
  `}
`

const Sort = styled.div`
  padding: 0 0 30px;
  ${Mixin.clearFix()}
`


const Compare = styled(Button)`
  padding: 8px 30px 5px;
  ${Media.desktop`
    float: right;
  `}
  ${Media.mobile`
    margin-top: 10px;
  `}
`

const CircleIcon = styled(Circle)`
  display: inline-block;
  position: relative;
  top: -2px;
  width: 20px;
  height: 18px;
  padding: 0;
  margin-left: 6px;
  color: ${Theme.colors.bright_red_new};
  background-color: ${Theme.colors.white};
  border: 1px solid ${Theme.colors.bright_red_new};
  vertical-align: middle;
`

const Count = styled.span`
  display: inline-block;
  position: relative;
  top: 2px;
  ${Mixin.font.GothamBold(12)}
`

const ViewAllSection = styled.div`
  padding: 6px 24px;
  line-height: 19px;
  letter-spacing: 0;
  background-color: ${Theme.colors.off_white};
  border: 1px solid ${Theme.colors.light_grey};
  color: ${Theme.colors.dark_grey};
  text-align: center;
  ${Mixin.font.GothamBold(14)}
  ${Mixin.responsive('padding', '20px 30px', '10px 20px')}
`

const View = styled(A)`
  margin-left: 5px;
  color: ${Theme.colors.blue};
  ${Mixin.font.GothamBold(14)}
  ${Mixin.responsive('padding', '10px 0', '4px 0 0 0')}
  ${Media.mobile`
    display: block;
  `}
`

const InfinitySpinnerIcon = styled(SpinnerIcon)`
  position: absolute;
  top: 30px;
  left: 50%;
`

const SpinnerWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 60px;
`

const NoResultBlock = styled.div`
  text-align: center;
  margin-top: 20px;
`

const NoResultTitle = styled.h1`
  ${Mixin.font.GothamBold(26)}
  line-height: 1;
  color: ${Theme.colors.black};
  letter-spacing: 0;
  text-transform: uppercase;
  margin: 0 0 20px;
`

const NoResultMessage = styled.div`
  margin-bottom: 5px;
`

const StyledButtonLink = styled(Link)`
  display: inline-block;
  margin: 15px;
`
